import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames, GastherapyViewerAliases } from '../../../../../codegen';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MedicationStatus } from '../../administration-schedule/administration-schedule-types';
import { MedicationAndDateHeaderParams } from '../../../../../controllers/commons/header/medication-and-date-header-params';
import { ActionAPI, BaseModalProps, ButtonAPI, DeckAPI, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { GastherapyItemStatus, GastherapyProps, GastherapyStatus, ObtainGasCourseResponse } from '../gastherapy-service';
import { HttpService, MessageService} from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../../constants/endpoints';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { Inject } from '@odinjs/odin';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { YesNoType } from '../../../../../utils/enum-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { ItemStatus } from '../../../../../constants/utils-adep';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import MedicationService from '../../../services/medication-service';
import GenericService from '../../../../../services/generic-service';
import { ModalType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { DialogModalConfirm } from '../../../../adep/eletronic-prescription/restart-or-start-item-config/dialogModalConfirm';

@Controller({ component: GastherapyViewerAliases.GASTHERAPY_VIEWER_LAYOUT, fragment: FragmentNames.GASTHERAPY_VIEWER })
export default class GastherapyViewerController extends BaseController {

  @Inject(GastherapyViewerAliases.PANEL_GASTHERAPY_VIEWER_CONTENT)
  private contentPanelApi!: PanelAPI;

  @Inject(GastherapyViewerAliases.INTERRUPT_BUTTON)
  private interruptButtonApi!: ButtonAPI;

  @Inject(GastherapyViewerAliases.EXECUTE_TIME_BUTTON)
  private executeTimeButtonApi!: ButtonAPI;

  @OnMount(GastherapyViewerAliases.GASTHERAPY_VIEWER_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    this.contentPanelApi.setVisible(false);
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const timelineItemDetail = TimelineContext.get() as TimeLineEventMedication;
    MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
      medicationName: timelineItemDetail.description,
      scheduleDate: timelineItemDetail.start,
      type: 'GASTHERAPY'
    };
    this.fetchInfoGastherapy().then((response) => {
      const infoGastherapy = response as GastherapyProps;
      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
      };
      this.context.set({ ...infoGastherapy, encounterId, patientId });
    }).finally(() => {
      this.contentPanelApi.setVisible(true);
    })
  }

  @OnMount(GastherapyViewerAliases.REVERT_GASTHERAPY_ACTION)
  handleMountActionReverse(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    const { itemStatus } = TimelineContext.get();
    actionApi.setEnabled(this.isAllowedToReverse() && ![String(GastherapyItemStatus.NOT_STARTED)].includes(itemStatus));
  }

  @OnClick(GastherapyViewerAliases.REVERT_GASTHERAPY_ACTION)
  handleClickActionReverse(): void {
    const reverseExpression = 'text.revert-last-event';
    this.context.set({ actionType: GastherapyItemStatus.REVERSE });
    this.reverteOrInterruptModal(reverseExpression);
  }

  private reverteOrInterruptModal(expression: string): void {
    const contextItem =  this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: {contextItem},
      focusOnOpen: false,
      fragment: FragmentNames.GASTHERAPY_REVERSE_OR_INTERRUPT,
      id: FragmentNames.GASTHERAPY_REVERSE_OR_INTERRUPT,
      onBeforeClose: (event) => DialogModalConfirm(event, FragmentNames.GASTHERAPY_REVERSE_OR_INTERRUPT),
      title: expression,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  private isAllowedToReverse(): boolean {
    const allowRevertParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_REVERSE_LEST_EVENT_GASTHERAPY)
    if (allowRevertParam !== YesNoType.YES) {
      return false
    }
    const { hourStatus } = this.historyService.getCurrentRouteParams();
    return hourStatus !== MedicationStatus.PENDING.valueOf();
  }

  @OnMount(GastherapyViewerAliases.GASTHERAPY_VIEWER_DECK)
  handleMountDeck({ target }: EventData<DeckAPI>): void {
    const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
    const infoGastherapy = this.context.getBulkData() as unknown as GastherapyProps;
    const unitOfMeasurement = infoGastherapy.unitOfMeasurement != null ? infoGastherapy.unitOfMeasurement : '';
    const infoQuantity = infoGastherapy.quantity != null ? String(infoGastherapy.quantity)?.concat(' ' + unitOfMeasurement) : '';

    target.setData([{
      breathingType: infoGastherapy.breathingType,
      description: timelineGastherapy.description,
      itemQuantity: infoQuantity,
      prescriptionNumber: timelineGastherapy.prescriptionNumber,
      qtOxygenFraction: infoGastherapy.qtOxygenFraction,
      respiratoryDevice: infoGastherapy.deviceDescription,
      respiratoryModality: infoGastherapy.respiratoryModality,
      scheduleDate: timelineGastherapy.start,
      statusItem: timelineGastherapy.status
    }]);
  }

  private fetchInfoGastherapy(): Promise<GastherapyProps> {
    const { cpoeSequence } = TimelineContext.get();
    return HttpService.get<GastherapyProps>(Endpoint.EMAR_GET_GASTHERAPY_INFORMATION, {
      gastherapyId: cpoeSequence,
    }).then((response) => {
      const responseBody = response.body as GastherapyProps;
      return responseBody;
    })
  }

  @OnMount(GastherapyViewerAliases.EXECUTE_TIME_BUTTON)
  handleMountExecuteTimeButton(): void {
    this.validateAndSetButtonVisibility();
  }

  private validateAndSetButtonVisibility(): void {
    const { itemStatus, status} = TimelineContext.get();

    if (status === GastherapyStatus.STARTED) {
      this.executeTimeButtonApi.setVisible(false);
      return;
    }
    this.setScreenLabelDefault(itemStatus, status);
  }

  private setScreenLabelDefault(itemStatus: string, status: string): void {
    const label = this.setScreenLabel(status, itemStatus);
    this.setExecuteTimeButton(label)
  }

  private setScreenLabel(status: string, itemStatus: string): string {
    if ([String(GastherapyStatus.PENDING), GastherapyStatus.INTERRUPTED].includes(status) && itemStatus === GastherapyItemStatus.INTERRUPTED) {
      this.executeTimeButtonApi.setVisible(true);
      return 'button.infusion-viewer.restart'
    }

    if (status !== GastherapyStatus.STARTED) {
      this.executeTimeButtonApi.setVisible(true);
      return 'text.execute-gastherapy'
    }
    return ''
  }

  private setExecuteTimeButton(label: string): void {
    this.executeTimeButtonApi.setValue(MessageService.translate(label));
    this.executeTimeButtonApi.setVisible(true);
  }

  @OnClick(GastherapyViewerAliases.EXECUTE_TIME_BUTTON)
  handleOnClickExecuteTimeButton(): void {
    this.obtainGasCourse();
  }

  @OnMount(GastherapyViewerAliases.INTERRUPT_BUTTON)
  handleOnMountInterruptButton(): void {
    const { itemStatus } = TimelineContext.get();
    const isValidItemStatusForInterrupt = [GastherapyItemStatus.STARTED, GastherapyItemStatus.RESTARTED].some(status => status === itemStatus);
    this.interruptButtonApi.setVisible(isValidItemStatusForInterrupt);
    this.executeTimeButtonApi.setVisible(!isValidItemStatusForInterrupt);
  }

  @OnClick(GastherapyViewerAliases.INTERRUPT_BUTTON)
  handleOnClickInterruptButton(): void {
    const interruptExpression = 'button.infusion-viewer.interrupt';
    this.context.set({ actionType: GastherapyItemStatus.INTERRUPTED });
    this.reverteOrInterruptModal(interruptExpression);
  }

  @OnMount(GastherapyViewerAliases.TERMINATE_GASTHERAPY_ACTION)
  handleFinishGastherapyAction(event: MouseEvent): void {
    const actionApi = event.target as unknown as ActionAPI;
    actionApi.setEnabled(this.isStatusValidToTerminate());
  }

  private gastherapyExecuteTimeModal(): void {
    const { itemStatus } = TimelineContext.get();
    const startGastherapy = itemStatus === GastherapyItemStatus.NOT_STARTED ? 'text.execute-gastherapy' : 'button.infusion-viewer.restart';
    const scheduleTitleDescription = MessageService.translate(startGastherapy);
    const gastherapy = this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: { gastherapy },
      focusOnOpen: false,
      fragment: FragmentNames.GASTHERAPY_START_OR_RESTART_MODAL,
      id: FragmentNames.GASTHERAPY_START_OR_RESTART_MODAL,
      onBeforeClose: (event) => DialogModalConfirm(event, FragmentNames.GASTHERAPY_START_OR_RESTART_MODAL),
      title: scheduleTitleDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  private obtainGasCourse(): void {
    const obtainGasCourse = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.WHEN_STARTING_THE_GAS_THERAPY_CHECK_IF_THERE_IS_ALREADY_ONE_IN_PROGRESS) as string;

    if (obtainGasCourse === YesNoType.YES) {
      const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
      const { encounterId } = this.historyService.getCurrentRouteParams();
      const body: Record<string, Value> = {
        encounterId: Number(encounterId),
        prescriptionNumber: timelineGastherapy.prescriptionNumber,
        sequenceGastherapy: timelineGastherapy.itemCode
      }
      HttpService.post(Endpoint.EMAR_POST_OBTAIN_GAS_COURSE, {}, { body })
        .then((res) => {
          const msg = (res?.body ?? res) as ObtainGasCourseResponse;
          if(msg.message){
            const messages = Array.isArray(msg.message) ? msg.message : [msg.message];
            const cleanMessage = GenericService.replaceMessage(messages);
            cleanMessage.forEach((message: string) => MedicationService.showToast(message, ToastLevel.WARNING));
            return;
          }
          this.gastherapyExecuteTimeModal();
        });
    } else {
      this.gastherapyExecuteTimeModal();
    }
  }

  @OnMount(GastherapyViewerAliases.SUSPEND_GASTHERAPY_ACTION)
  handleMountSuspendNursingInterventionsAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    const { itemStatus } = TimelineContext.get();
    const allowToSuspendGastherapy = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_TO_SUSPEND_GASTHERAPY_ITEMS) as string;
    actionApi.setEnabled(BooleanUtils.isTrueValue(allowToSuspendGastherapy) && ![String(GastherapyItemStatus.STARTED),
      GastherapyItemStatus.RESTARTED, GastherapyItemStatus.TERMINATE].includes(itemStatus))
  }

  @OnClick(GastherapyViewerAliases.SUSPEND_GASTHERAPY_ACTION)
  handleClickSuspendNursingInterventionsAction(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.GASTHERAPY_SUSPEND,
      id: FragmentNames.GASTHERAPY_SUSPEND,
      title: 'label.suspend-medication.title',
    })
  }

  @OnClick(GastherapyViewerAliases.TERMINATE_GASTHERAPY_ACTION)
  handleTerminateGastherapyClick(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.GASTHERAPY_TERMINATE,
      id: FragmentNames.GASTHERAPY_TERMINATE,
      title: 'text.item-terminate',
    })
  }

  private isStatusValidToTerminate(): boolean {
    const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
    const itemStatus = timelineGastherapy.itemStatus as ItemStatus;
    return [ItemStatus.STARTED, ItemStatus.INTERRUPTED, ItemStatus.RESTARTED, ItemStatus.EXPIRED].includes(itemStatus);
  }
}
